import type { Context } from './context.js';

export enum TextureType {
    e2D = '2d',
    e3D = '3d',
}

export interface TextureCreateInfo {
    type: TextureType;
    /** Width, height, depth (depth=1 for 2D). */
    extent: { width: number; height: number; depthOrArrayLayers?: number };
    format: GPUTextureFormat;
    usage: GPUTextureUsageFlags;
    mipLevelCount?: number;
    sampleCount?: number;
    label?: string;
}

export class Texture {
    readonly handle: GPUTexture;
    readonly view: GPUTextureView;
// integrity-allow: cat2.public-symbol-used-ts; pre-v1 Stack B public symbol with no current consumer (tracked for v1.1 review per grandfather-catalog cat2-stack-b-unused); n/a
    readonly format: GPUTextureFormat;
// integrity-allow: cat2.public-symbol-used-ts; pre-v1 Stack B public symbol with no current consumer (tracked for v1.1 review per grandfather-catalog cat2-stack-b-unused); n/a
    readonly type: TextureType;
// integrity-allow: cat2.public-symbol-used-ts; pre-v1 Stack B public symbol with no current consumer (tracked for v1.1 review per grandfather-catalog cat2-stack-b-unused); n/a
    readonly width: number;
// integrity-allow: cat2.public-symbol-used-ts; pre-v1 Stack B public symbol with no current consumer (tracked for v1.1 review per grandfather-catalog cat2-stack-b-unused); n/a
    readonly height: number;
// integrity-allow: cat2.public-symbol-used-ts; pre-v1 Stack B public symbol with no current consumer (tracked for v1.1 review per grandfather-catalog cat2-stack-b-unused); n/a
    readonly depth: number;
    private ctx: Context;

    constructor(ctx: Context, info: TextureCreateInfo) {
        this.ctx = ctx;
        this.type = info.type;
        this.format = info.format;
        this.width  = info.extent.width;
        this.height = info.extent.height;
        this.depth  = info.extent.depthOrArrayLayers ?? 1;

        const desc: GPUTextureDescriptor = {
            size: {
                width: info.extent.width,
                height: info.extent.height,
                depthOrArrayLayers: info.extent.depthOrArrayLayers ?? 1,
            },
            format: info.format,
            usage: info.usage,
            dimension: info.type === TextureType.e3D ? '3d' : '2d',
        };
        if (info.mipLevelCount !== undefined) desc.mipLevelCount = info.mipLevelCount;
        if (info.sampleCount !== undefined) desc.sampleCount = info.sampleCount;
        if (info.label) desc.label = info.label;

        this.handle = ctx.device.createTexture(desc);
        const viewDesc: GPUTextureViewDescriptor = {
            dimension: info.type === TextureType.e3D ? '3d' : '2d',
        };
        if (info.label) viewDesc.label = `${info.label}-view`;
        this.view = this.handle.createView(viewDesc);
    }

    /**
     * Upload pixel data into a 2D texture at mip level 0. Pitch (bytesPerRow)
     * is computed from the format; pass `bytesPerPixel` if WebGPU's default
     * doesn't match (for non-standard formats). For most use cases the
     * default rgba8unorm / r32float / etc. work fine.
     */
    uploadDirect2D(src: ArrayBufferView, bytesPerPixel: number): void {
        if (this.type !== TextureType.e2D) {
            throw new Error('Texture: uploadDirect2D requires a 2D texture');
        }
        this.ctx.device.queue.writeTexture(
            { texture: this.handle, mipLevel: 0 },
            src.buffer,
            {
                offset: src.byteOffset,
                bytesPerRow: this.width * bytesPerPixel,
                rowsPerImage: this.height,
            },
            { width: this.width, height: this.height, depthOrArrayLayers: 1 },
        );
    }

    /**
     * Async readback of a 2D texture's mip-0 contents into a CPU Uint8Array.
     * Mirrors Buffer.readback. Caller specifies bytesPerPixel (e.g. 8 for
     * rg32float, 4 for r32float, 4 for rgba8unorm).
     *
     * Note: WebGPU requires bytesPerRow to be a multiple of 256. For widths
     * that don't satisfy this with the given bytesPerPixel, the caller must
     * pad rows manually or pick a different format. Throws if mismatch is
     * detectable.
     */
    async readback2D(bytesPerPixel: number): Promise<Uint8Array> {
        if (this.type !== TextureType.e2D) {
            throw new Error('Texture: readback2D requires a 2D texture');
        }
        const bytesPerRow = this.width * bytesPerPixel;
        if (bytesPerRow % 256 !== 0) {
            throw new Error(
                `Texture: readback2D requires width*bytesPerPixel to be a `
                + `multiple of 256 (got ${bytesPerRow}). Pad rows or pick a `
                + `different width.`
            );
        }
        const stagingDesc: GPUBufferDescriptor = {
            size: bytesPerRow * this.height,
            usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
        };
        if (this.handle.label) stagingDesc.label = `${this.handle.label}-readback`;
        const staging = this.ctx.device.createBuffer(stagingDesc);
        const encoder = this.ctx.device.createCommandEncoder();
        encoder.copyTextureToBuffer(
            { texture: this.handle, mipLevel: 0 },
            { buffer: staging, bytesPerRow, rowsPerImage: this.height },
            { width: this.width, height: this.height, depthOrArrayLayers: 1 },
        );
        this.ctx.device.queue.submit([encoder.finish()]);
        await staging.mapAsync(GPUMapMode.READ);
        const copy = new Uint8Array(staging.getMappedRange().slice(0));
        staging.unmap();
        staging.destroy();
        return copy;
    }

    destroy(): void {
        this.handle.destroy();
    }
}
